import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Undo2, Check, X } from "lucide-react";

const STATUSES = ["all", "pending", "approved", "rejected"];
const STATUS_STYLES = {
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rejected: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function Returns() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const canApprove = ["admin", "manager"].includes(user.role);

  const load = () => {
    const params = status === "all" ? {} : { status };
    api.get("/returns", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [status]);

  const act = async (id, action) => {
    try {
      const { data } = await api.post(`/returns/${id}/${action}`, action === "approve" ? {} : {});
      toast.success(action === "approve" ? `Approved · refund ${fmtCurrency(data.refund)}` : "Rejected");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="returns-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">After-sales</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Returns</h1>
          <p className="text-sm text-zinc-500 mt-2">Per-unit serial-tracked returns. Manager approval restocks the units and adjusts the invoice.</p>
        </div>
      </div>

      <div className="flex gap-2">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`returns-filter-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${status === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>{s}</button>
        ))}
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-semibold">Requested</th>
              <th className="text-left px-5 py-3 font-semibold">Invoice #</th>
              <th className="text-left px-5 py-3 font-semibold">Part #</th>
              <th className="text-right px-5 py-3 font-semibold">Qty</th>
              <th className="text-left px-5 py-3 font-semibold">Reason</th>
              <th className="text-right px-5 py-3 font-semibold">Refund</th>
              <th className="text-left px-5 py-3 font-semibold">By</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-5 py-12 text-center text-sm text-zinc-500"><Undo2 className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No returns yet.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`return-row-${r.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDate(r.requested_at)}</td>
                <td className="px-5 py-3"><Link to={`/invoices/${r.invoice_id}`} className="font-mono text-zinc-950 hover:underline">{r.invoice_number}</Link></td>
                <td className="px-5 py-3 font-mono text-zinc-700">{r.part_number}</td>
                <td className="px-5 py-3 text-right font-mono">{r.qty}</td>
                <td className="px-5 py-3 text-xs text-zinc-500 max-w-xs truncate">{r.reason || "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtCurrency(r.refund_amount ?? r.refund_amount_estimate)}</td>
                <td className="px-5 py-3 text-zinc-700">{r.requested_by_name}</td>
                <td className="px-5 py-3"><span className={`text-[11px] uppercase tracking-wider px-2 py-1 border ${STATUS_STYLES[r.status]}`}>{r.status}</span></td>
                <td className="px-5 py-3 text-right space-x-1">
                  {canApprove && r.status === "pending" && (
                    <>
                      <button data-testid={`return-approve-${r.id}`} onClick={() => act(r.id, "approve")} className="text-xs uppercase tracking-wider px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"><Check className="w-3 h-3" />Approve</button>
                      <button data-testid={`return-reject-${r.id}`} onClick={() => act(r.id, "reject")} className="text-xs uppercase tracking-wider px-2 py-1 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"><X className="w-3 h-3" />Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
